/**
 * Brand Template Configuration
 *
 * Maps brand keys to their default document template asset paths.
 * Templates are static assets stored in public/brands/{brand}/templates/.
 * These serve as the brand-level defaults in the template resolution chain:
 *   User override (IndexedDB) → Admin template → Brand default (static asset) → Programmatic export
 */

export type TemplateFormat = "word" | "pptx" | "xlsx";

export interface BrandTemplate {
  /** Unique ID for this template */
  id: string;
  /** Display name */
  name: string;
  /** Template format */
  format: TemplateFormat;
  /** Path to the template file (relative to public/) */
  path: string;
  /** Short description of the template's purpose */
  description?: string;
  /** Whether this is the default template for the format */
  isDefault?: boolean;
}

export interface BrandTemplateConfig {
  /** All available templates for this brand */
  templates: BrandTemplate[];
}

/**
 * Legacy single-path accessors for backward compatibility.
 * Returns the path of the default template for the given format.
 */
export function getDefaultTemplatePath(
  brandKey: string,
  format: TemplateFormat,
): string | undefined {
  const config = BRAND_TEMPLATES[brandKey];

  if (!config) return undefined;

  const defaultTemplate = config.templates.find((t) => t.format === format && t.isDefault);

  return defaultTemplate?.path;
}

/**
 * Get all templates for a brand and format.
 */
export function getTemplatesForFormat(brandKey: string, format: TemplateFormat): BrandTemplate[] {
  const config = BRAND_TEMPLATES[brandKey];

  if (!config) return [];

  return config.templates.filter((t) => t.format === format);
}

/**
 * Get a specific template by ID.
 */
export function getTemplateById(brandKey: string, templateId: string): BrandTemplate | undefined {
  const config = BRAND_TEMPLATES[brandKey];

  if (!config) return undefined;

  return config.templates.find((t) => t.id === templateId);
}

/**
 * Brand template mappings.
 * Keys should match the brand key used in the brand configuration.
 */
export const BRAND_TEMPLATES: Record<string, BrandTemplateConfig> = {
  ngv: {
    templates: [
      {
        id: "ngv-pptx-default",
        name: "NGV Presentation",
        format: "pptx",
        path: "/brands/ngv/templates/ngv-presentation.pptx",
        description: "Standard NGV branded presentation",
        isDefault: true,
      },
      {
        id: "ngv-word-default",
        name: "NGV Report",
        format: "word",
        path: "/brands/ngv/templates/ngv-report.docx",
        description: "Standard NGV branded report",
        isDefault: true,
      },
      {
        id: "ngv-xlsx-default",
        name: "NGV Data Export",
        format: "xlsx",
        path: "/brands/ngv/templates/ngv-data.xlsx",
        description: "Standard NGV branded spreadsheet with cover page",
        isDefault: true,
      },
    ],
  },
  // webuild: no brand default templates — users can upload their own via Settings → Exports
};

/**
 * Resolve the brand key from a Brand object.
 * Handles different brand name formats to find the correct template config.
 */
export function resolveBrandKey(brandName: string): string | undefined {
  const normalised = brandName.toLowerCase().replace(/\s+/g, "");

  // Direct match
  if (BRAND_TEMPLATES[normalised]) return normalised;

  // Known mappings
  const mappings: Record<string, string> = {
    nationalgridventuresai: "ngv",
    nationalgridventures: "ngv",
    ngv: "ngv",
    "webuild-ai": "webuild",
    "webuild-aiazure": "webuild",
    webuildai: "webuild",
    webuild: "webuild",
    investai: "webuild",
  };

  return mappings[normalised];
}
